import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Paper, Typography, Stack, Chip, Button, IconButton,
  Tooltip, CircularProgress, Divider,
} from '@mui/material';
import {
  Close, Medication as MedIcon, Print, GridOn, Description,
} from '@mui/icons-material';
import axios from 'axios';
import moment from 'moment';
import { getMedicationSheetHistorydb } from '../BaseURL';

// ── Theme tokens ──────────────────────────────────────────────────────
const PRIMARY      = '#1A9B7F';
const PRIMARY_DARK = '#157763';
const PRIMARY_BG   = 'rgba(26,155,127,0.10)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

// Prefer canonical due_at; fall back to legacy `${d_dat} ${time}`.
const dueMoment = (rec) => {
  if (rec?.due_at) {
    const m = moment(rec.due_at);
    if (m.isValid()) return m;
  }
  return moment(`${rec?.d_dat || ''} ${rec?.time || ''}`, 'YYYY-MM-DD hh:mm:ss A');
};

const statusOf = (rec) => {
  const isServed = rec.s_by && rec.s_by !== '-' && rec.s_by !== 'i';
  if (isServed) return 'served';
  if (dueMoment(rec).isBefore(moment())) return 'missed';
  return 'pending';
};

const TONE = {
  served:  { bg: '#DCFCE7', border: '#86EFAC', icon: '✓', color: '#15803D' },
  missed:  { bg: '#FEE2E2', border: '#FCA5A5', icon: '⚠', color: '#991B1B' },
  pending: { bg: '#FEF3C7', border: '#FCD34D', icon: '⏳', color: '#A16207' },
};

const initialsOf = (name = '') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '—';

/**
 * Printable medication administration sheet — shared by the nurse panels and
 * the doctor's Ward Rounds dialog. Reads med_sheet rows for the patient and
 * lays them out as the same MAR grid (one row per drug, dose chips per slot)
 * shown inline. Three export actions: Print/PDF, Word (.doc HTML), Excel (.xls HTML).
 *
 * Props:
 *   open, onClose
 *   patient   { id, fullName, age, gender, visitDate }
 */
export default function MedicationAdministrationSheet({ open, onClose, patient }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows]       = useState([]);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!open || !patient?.id) return;
    setLoading(true); setError(null);
    axios.get(getMedicationSheetHistorydb, {
      params: {
        patientId: patient.id,
        startDate: patient.visitDate || '1900-01-01',
        endDate:   moment().format('YYYY-MM-DD'),
      },
    })
      .then(res => {
        if (res.data?.success) setRows(res.data.medicationSheet || []);
        else { setError(res.data?.message || 'Failed to load'); setRows([]); }
      })
      .catch(err => {
        console.error('loadMedicationSheet failed', err);
        setError('Could not load medication sheet.');
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [open, patient?.id, patient?.visitDate]);

  // Group by drug name (first word of `med`).
  const grouped = useMemo(() => {
    const map = new Map();
    rows.forEach(rec => {
      const fullMed = rec.med || '';
      const drug = fullMed.split(' ')[0] || 'Unknown';
      const detail = fullMed.slice(drug.length).trim();
      if (!map.has(drug)) map.set(drug, { drug, detail, doses: [] });
      map.get(drug).doses.push(rec);
    });
    const arr = [...map.values()];
    arr.forEach(g => g.doses.sort((a, b) => dueMoment(a).valueOf() - dueMoment(b).valueOf()));
    return arr;
  }, [rows]);

  const totals = useMemo(() => {
    const all = rows || [];
    const meds = new Set(all.map(r => r.medication_trans_code || r.med));
    const served = all.filter(r => statusOf(r) === 'served').length;
    const dueNow = all.filter(r => statusOf(r) === 'pending').length;
    const missed = all.filter(r => statusOf(r) === 'missed').length;
    return { meds: meds.size, all: all.length, served, dueNow, missed };
  }, [rows]);

  // Distinct nurses involved (as charters and as servers).
  const nurses = useMemo(() => {
    const charters = new Set();
    const servers  = new Set();
    rows.forEach(r => {
      if (r.user) charters.add(r.user);
      if (r.s_by && r.s_by !== '-' && r.s_by !== 'i') servers.add(r.s_by);
    });
    return { charters: [...charters], servers: [...servers] };
  }, [rows]);

  // ── Build printable HTML for export/print ─────────────────────────────
  const buildPrintableHtml = () => {
    const styles = `
      <style>
        body{font-family:Inter,Segoe UI,Arial,sans-serif;color:${STRONG};margin:24px;}
        h1{font-size:18px;margin:0 0 4px 0;letter-spacing:0.5px;text-transform:uppercase;}
        .sub{font-size:12px;color:${MUTED};margin:0 0 16px 0;}
        .meta{font-size:12px;color:${STRONG};margin-bottom:16px;border:1px solid ${BORDER};padding:10px 12px;border-radius:6px;background:#f8fafc;}
        .meta strong{color:${MUTED};font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;margin-right:6px;}
        table{width:100%;border-collapse:collapse;font-size:12px;}
        th,td{border:1px solid ${BORDER};padding:6px 8px;vertical-align:top;}
        th{background:#f1f5f9;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:0.5px;color:${MUTED};}
        .drug{font-weight:700;color:${STRONG};}
        .detail{color:${MUTED};font-size:11px;}
        .chip{display:inline-block;padding:3px 6px;border-radius:4px;font-size:11px;font-weight:600;margin:2px;border:1px solid;}
        .served{background:${TONE.served.bg};border-color:${TONE.served.border};color:${TONE.served.color};}
        .missed{background:${TONE.missed.bg};border-color:${TONE.missed.border};color:${TONE.missed.color};}
        .pending{background:${TONE.pending.bg};border-color:${TONE.pending.border};color:${TONE.pending.color};}
        .footer{margin-top:20px;font-size:11px;color:${MUTED};border-top:1px dashed ${BORDER};padding-top:10px;}
        .footer .row{margin-bottom:4px;}
        .footer .row strong{color:${STRONG};margin-right:6px;}
        .sigs{margin-top:30px;display:flex;gap:40px;font-size:12px;}
        .sig{flex:1;border-top:1px solid #94a3b8;padding-top:6px;text-align:center;color:${MUTED};}
        @media print{body{margin:12mm;}}
      </style>
    `;
    const headerHtml = `
      <h1>Medication Administration Sheet</h1>
      <p class="sub">Generated ${moment().format('ddd, D MMM YYYY · hh:mm A')}</p>
      <div class="meta">
        <strong>Patient</strong>${patient?.fullName || '—'}
        &nbsp;·&nbsp; <strong>ID</strong>${patient?.id || '—'}
        &nbsp;·&nbsp; <strong>Age</strong>${patient?.age || '—'}
        &nbsp;·&nbsp; <strong>Sex</strong>${patient?.gender || '—'}
        &nbsp;·&nbsp; <strong>Admission</strong>${patient?.visitDate || '—'}
      </div>
    `;

    const tableHtml = `
      <table>
        <thead>
          <tr>
            <th style="width:240px">Medication</th>
            <th>Dose schedule</th>
          </tr>
        </thead>
        <tbody>
          ${grouped.map(g => {
            const served = g.doses.filter(d => statusOf(d) === 'served').length;
            const missed = g.doses.filter(d => statusOf(d) === 'missed').length;
            const pending= g.doses.length - served - missed;
            const summary = [
              served ? `${served} given` : null,
              pending ? `${pending} due`  : null,
              missed ? `${missed} missed` : null,
            ].filter(Boolean).join(' · ');
            return `
              <tr>
                <td>
                  <div class="drug">${g.drug}</div>
                  <div class="detail">${g.detail || '—'}</div>
                  <div class="detail" style="margin-top:4px;">${summary}</div>
                </td>
                <td>
                  ${g.doses.map(d => {
                    const status = statusOf(d);
                    const m = dueMoment(d);
                    const tip = status === 'served'
                      ? `${m.format('hh:mm A · D MMM')} · ${d.s_by || '—'}`
                      : `${m.format('hh:mm A · D MMM')}`;
                    return `<span class="chip ${status}" title="${tip}">
                      ${TONE[status].icon} ${m.format('HH:mm')} <span style="opacity:.7;">${m.format('D MMM')}</span>
                      ${status === 'served' && d.s_by ? ` · ${d.s_by}` : ''}
                    </span>`;
                  }).join('')}
                </td>
              </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    `;

    const footerHtml = `
      <div class="footer">
        <div class="row"><strong>Total entries:</strong>${totals.all}</div>
        <div class="row"><strong>Served:</strong>${totals.served}
          &nbsp;·&nbsp; <strong>Pending:</strong>${totals.dueNow}
          &nbsp;·&nbsp; <strong>Missed:</strong>${totals.missed}</div>
        <div class="row"><strong>Charted by:</strong>${nurses.charters.join(', ') || '—'}</div>
        <div class="row"><strong>Served by:</strong>${nurses.servers.join(', ')   || '—'}</div>
      </div>
      <div class="sigs">
        <div class="sig">Reviewed by (Doctor)</div>
        <div class="sig">Verified by (Charge Nurse)</div>
      </div>
    `;

    return `<!doctype html><html><head><meta charset="utf-8"><title>MAR · ${patient?.fullName || ''}</title>${styles}</head><body>${headerHtml}${tableHtml}${footerHtml}</body></html>`;
  };

  // Print via a hidden iframe — survives popup blockers and doesn't steal
  // focus from the dialog. The iframe is removed after print returns/cancels.
  const handlePrint = () => {
    const html = buildPrintableHtml();
    const iframe = document.createElement('iframe');
    Object.assign(iframe.style, {
      position: 'fixed', right: 0, bottom: 0,
      width: 0, height: 0, border: 0, opacity: 0,
    });
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      console.error('Print iframe has no document');
      document.body.removeChild(iframe);
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();

    let printed = false;
    const triggerPrint = () => {
      if (printed) return;
      printed = true;
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error('print failed', e);
      }
      // Cleanup after the user has had time to confirm/cancel.
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 1500);
    };

    // Some browsers fire onload synchronously (after document.close), others
    // need a tick — handle both, with a hard fallback.
    if (iframe.contentWindow?.document?.readyState === 'complete') {
      triggerPrint();
    } else {
      iframe.onload = triggerPrint;
      setTimeout(triggerPrint, 500);
    }
  };

  const downloadBlob = (mime, ext) => {
    const html = buildPrintableHtml();
    const blob = new Blob(['\ufeff' + html], { type: mime });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `MAR_${patient?.id || 'patient'}_${moment().format('YYYYMMDD_HHmm')}.${ext}`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  const handleExportWord  = () => downloadBlob('application/msword', 'doc');
  const handleExportExcel = () => downloadBlob('application/vnd.ms-excel', 'xls');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '88vh' } }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, py: 2,
        bgcolor: PRIMARY, color: 'white',
      }}>
        <MedIcon />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
            Medication Administration Sheet
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
            {patient?.fullName ? `${patient.fullName} · ID ${patient.id}` : 'Select a patient'}
            {patient?.age != null ? ` · ${patient.age} yrs` : ''}
            {patient?.gender ? ` · ${patient.gender}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, bgcolor: '#f8fafc' }}>
        {/* Summary strip */}
        <Box sx={{
          mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center',
        }}>
          <SummaryPill label="Total"    value={totals.all}    color={STRONG} tint="#f1f5f9" />
          <SummaryPill label="Served"   value={totals.served} color="#15803D" tint="rgba(22,163,74,0.10)" />
          <SummaryPill label="Pending"  value={totals.dueNow} color="#A16207" tint="rgba(217,119,6,0.10)" />
          <SummaryPill label="Missed"   value={totals.missed} color="#991B1B" tint="rgba(220,38,38,0.10)" />
          <SummaryPill label="Meds"     value={totals.meds}   color={PRIMARY}  tint={PRIMARY_BG} />
        </Box>

        {/* Body */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: PRIMARY }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: '#991B1B', fontWeight: 700 }}>{error}</Typography>
          </Box>
        ) : grouped.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <MedIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
            <Typography sx={{ color: MUTED, fontSize: '0.92rem', fontWeight: 700 }}>
              No medication sheet entries on file for this patient
            </Typography>
          </Box>
        ) : (
          <Paper elevation={0} sx={{
            borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff', overflow: 'hidden',
          }}>
            <Box sx={{ maxHeight: '52vh', overflowY: 'auto', overflowX: 'hidden' }}>
              {grouped.map((g, gi) => {
                const served  = g.doses.filter(d => statusOf(d) === 'served').length;
                const missed  = g.doses.filter(d => statusOf(d) === 'missed').length;
                const pending = g.doses.length - served - missed;
                return (
                  <Box key={g.drug + gi} sx={{
                    display: 'flex',
                    borderBottom: gi === grouped.length - 1 ? 'none' : `1px solid ${BORDER}`,
                    '&:hover': { bgcolor: '#f8fafc' },
                  }}>
                    <Box sx={{
                      width: 240, flexShrink: 0, p: 1.5,
                      borderRight: `1px solid ${BORDER}`, bgcolor: '#fff',
                      position: 'sticky', left: 0, zIndex: 1,
                    }}>
                      <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }}>
                        {g.drug}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.25 }}>
                        {g.detail || '—'}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                        {served > 0 && <Tag label={`${served} given`}  bg="rgba(22,163,74,0.10)" color="#15803D" />}
                        {pending > 0 && <Tag label={`${pending} due`}   bg="rgba(217,119,6,0.10)" color="#A16207" />}
                        {missed > 0 && <Tag label={`${missed} missed`}  bg="rgba(220,38,38,0.10)" color="#991B1B" />}
                      </Stack>
                    </Box>

                    <Box sx={{
                      flex: 1, minWidth: 0, p: 1,
                      display: 'flex', gap: 0.75, overflowX: 'auto', alignItems: 'center',
                    }}>
                      {g.doses.map((d, di) => {
                        const status = statusOf(d);
                        const tone = TONE[status];
                        const m = dueMoment(d);
                        const servedAt = d.served_at && moment(d.served_at).isValid()
                          ? moment(d.served_at).format('hh:mm A') : null;
                        const tip = (
                          <Box sx={{ p: 0.5, fontSize: '0.78rem', lineHeight: 1.5 }}>
                            <div><strong>{m.format('ddd D MMM, hh:mm A')}</strong></div>
                            <div>Status: <strong style={{ textTransform: 'capitalize' }}>{status}</strong></div>
                            {status === 'served' && (
                              <>
                                <div>Given by: {d.s_by}</div>
                                {servedAt && <div>At: {servedAt}</div>}
                              </>
                            )}
                            <div style={{ opacity: 0.7 }}>Charted by: {d.user || '—'}</div>
                          </Box>
                        );

                        return (
                          <Tooltip key={d.code || di} title={tip} arrow placement="top">
                            <Box sx={{
                              flexShrink: 0, minWidth: 96,
                              px: 1, py: 0.75, borderRadius: 1.5,
                              bgcolor: tone.bg, border: `1px solid ${tone.border}`,
                              display: 'flex', alignItems: 'center', gap: 0.75,
                            }}>
                              <Typography sx={{ fontSize: '0.85rem', lineHeight: 1 }}>{tone.icon}</Typography>
                              <Box>
                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: tone.color, lineHeight: 1.1 }}>
                                  {m.format('HH:mm')}
                                </Typography>
                                <Typography sx={{ fontSize: '0.6rem', color: tone.color, opacity: 0.85, lineHeight: 1.1 }}>
                                  {m.format('D MMM')}
                                </Typography>
                                {status === 'served' && d.s_by && (
                                  <Typography sx={{ fontSize: '0.55rem', color: tone.color, opacity: 0.85, lineHeight: 1.1, mt: 0.25, fontWeight: 700 }}>
                                    {initialsOf(d.s_by)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}

        {/* Signature footer */}
        {!loading && !error && rows.length > 0 && (
          <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
            <Typography sx={{ ...LABEL_SX, mb: 0.5 }}>Audit</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} sx={{ fontSize: '0.78rem', color: STRONG }}>
              <Box>
                <Box component="span" sx={{ color: MUTED, fontWeight: 700, mr: 0.5 }}>Charted by:</Box>
                {nurses.charters.join(', ') || '—'}
              </Box>
              <Divider flexItem orientation="vertical" sx={{ display: { xs: 'none', sm: 'block' } }} />
              <Box>
                <Box component="span" sx={{ color: MUTED, fontWeight: 700, mr: 0.5 }}>Served by:</Box>
                {nurses.servers.join(', ') || '—'}
              </Box>
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, bgcolor: '#fff', borderTop: `1px solid ${BORDER}`, gap: 0.75 }}>
        <Button
          variant="outlined"
          startIcon={<Description />}
          onClick={handleExportWord}
          disabled={loading || !rows.length}
          sx={{ textTransform: 'none', fontWeight: 700, color: '#2563EB', borderColor: '#bfdbfe', '&:hover': { borderColor: '#2563EB', bgcolor: 'rgba(37,99,235,0.06)' } }}
        >
          Word
        </Button>
        <Button
          variant="outlined"
          startIcon={<GridOn />}
          onClick={handleExportExcel}
          disabled={loading || !rows.length}
          sx={{ textTransform: 'none', fontWeight: 700, color: '#15803D', borderColor: '#bbf7d0', '&:hover': { borderColor: '#15803D', bgcolor: 'rgba(22,163,74,0.06)' } }}
        >
          Excel
        </Button>
        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={handlePrint}
          disabled={loading || !rows.length}
          sx={{
            bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK },
            textTransform: 'none', fontWeight: 700,
          }}
        >
          Print / PDF
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ color: MUTED, textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Local helpers ─────────────────────────────────────────────────────
function SummaryPill({ label, value, color, tint }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.4, borderRadius: 1.25,
      bgcolor: tint, color,
    }}>
      <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, lineHeight: 1 }}>
        {value}
      </Typography>
    </Box>
  );
}
function Tag({ label, bg, color }) {
  return (
    <Box sx={{ px: 0.75, py: 0.15, borderRadius: 1, bgcolor: bg, color, fontSize: '0.62rem', fontWeight: 700 }}>
      {label}
    </Box>
  );
}
